// Data hooks for the Products module (list = product-summary report,
// drill-down = product-detail + movement history — Stuart's 2026-08-26 ruling,
// nav.ts; Unleashed-parity drill-down added 2026-08-26). Thin wrappers over
// hooks/useApi.ts's useApiQuery (the shared primitive every web module goal
// builds on), covering:
//   - services/api/src/modules/reporting/products.ts   (GET /reporting/product-summary,
//     GET /reporting/product-detail)
//   - services/api/src/modules/reporting/movements.ts   (GET /reporting/movements)
// Read-only masters lookups (sites/locations) are duplicated locally rather than
// imported from features/reports or features/settings — the same
// "independently wrap the read-only masters endpoints" convention every prior
// web goal used (features/reports/api.ts's header, features/silo-counts/api.ts).
import type { ProductType } from '@zebra/core';
import { useApiQuery } from '../../hooks/useApi';
import type { LocationRow, MovementLedgerResponse, Page, ProductDetailDto, ProductSummaryResponse, SiteRow } from './types';

/**
 * Every date-range filter in this feature holds `YYYY-MM-DD` strings
 * (`DatePickerInput`'s `onChange` always reports this `DateStringValue` form).
 * Duplicated from features/reports/api.ts's identical `rangeQuery` (same
 * self-contained-sibling convention as that file's own header) — expands
 * `from`/`to` to the start/end of their respective SAST business days
 * (Africa/Johannesburg, fixed UTC+2, no DST — BUILD_SPEC.md §4 item 7) before
 * they go out as query params, so a date-only `to` doesn't silently exclude the
 * rest of its own day on the UTC-timezone Lambda.
 */
export function rangeQuery(from?: string | null, to?: string | null): { from?: string; to?: string } {
  return {
    from: from ? `${from}T00:00:00.000+02:00` : undefined,
    to: to ? `${to}T23:59:59.999+02:00` : undefined,
  };
}

// ---------------------------------------------------------------------------
// Read-only masters lookups
// ---------------------------------------------------------------------------
export function useSites() {
  return useApiQuery<Page<SiteRow>>(['products', 'config', 'sites'], '/config/sites', { query: { limit: 200 } });
}

export function useLocations(params: { siteId?: string } = {}) {
  return useApiQuery<Page<LocationRow>>(['products', 'config', 'locations', params], '/config/locations', {
    query: { siteId: params.siteId, limit: 200 },
  });
}

// ---------------------------------------------------------------------------
// 1. Product summary (list screen) — GET /reporting/product-summary
// ---------------------------------------------------------------------------
export interface ProductSummaryParams {
  q?: string;
  type?: ProductType;
  siteId?: string;
  locationId?: string;
  /** Mirrors the endpoint's own three-way shape (reporting.ts's
   * `productSummaryQuerySchema`): absent/'true' = active only, 'all' = every
   * product. */
  isActive?: 'true' | 'all';
}

export function useProductSummary(params: ProductSummaryParams) {
  return useApiQuery<ProductSummaryResponse>(['products', 'reporting', 'product-summary', params], '/reporting/product-summary', {
    query: {
      q: params.q,
      type: params.type,
      siteId: params.siteId,
      locationId: params.locationId,
      isActive: params.isActive,
    },
  });
}

// ---------------------------------------------------------------------------
// 2. Product detail (detail page's header + headline numbers + inventory
//    section) — GET /reporting/product-detail?productId= (services/api/src/
//    modules/reporting/products.ts). Supersedes the old GET /config/products/:id
//    + GET /reporting/stock-on-hand pairing — one call now returns the master
//    row, the headline totals (allocated/available/on-purchase/last-cost) and
//    the per-site/per-location breakdown together (see types.ts's
//    `ProductDetailDto` header for the full rationale).
// ---------------------------------------------------------------------------
export function useProductDetail(productId: string | undefined) {
  return useApiQuery<ProductDetailDto>(['products', 'reporting', 'product-detail', productId], '/reporting/product-detail', {
    query: { productId },
    enabled: Boolean(productId),
  });
}

// ---------------------------------------------------------------------------
// 3. Movement history (detail page's transactions + costs tables) — GET /reporting/movements
//    always with withRunningBalance=true (only meaningful once productId is set,
//    which this hook requires).
// ---------------------------------------------------------------------------
export interface ProductMovementsParams {
  productId: string;
  siteId?: string;
  type?: string;
  from?: string | null;
  to?: string | null;
  cursor?: string;
  limit?: number;
}

export function useProductMovements(params: ProductMovementsParams) {
  return useApiQuery<MovementLedgerResponse>(['products', 'reporting', 'movements', params], '/reporting/movements', {
    query: {
      productId: params.productId,
      siteId: params.siteId,
      type: params.type,
      ...rangeQuery(params.from, params.to),
      cursor: params.cursor,
      limit: params.limit ?? 50,
      withRunningBalance: 'true',
    },
    enabled: Boolean(params.productId),
  });
}
